import { MapPin, Phone, Globe, ExternalLink, Lightbulb, HelpCircle, ShieldAlert } from "lucide-react";
import { EvidenceBadge } from "../Badges";
import { FindingRow } from "./FindingRow";
import ContactsSection from "./ContactsSection";
import { CATEGORY_ORDER } from "../../lib/constants";
import { fmtDate, fmtDateTime } from "../../lib/api";

const Section = ({ title, children, testId, eyebrow }) => (
  <section className="print-card rounded-lg border bg-white p-5 shadow-sm sm:p-6" data-testid={testId}>
    {eyebrow && <div className="text-[10px] font-semibold uppercase tracking-widest text-orange-600">{eyebrow}</div>}
    <h2 className="mb-4 text-lg font-bold tracking-tight">{title}</h2>
    {children}
  </section>
);

const Overview = ({ r, loc }) => (
  <Section title="Restaurant overview & exact location" eyebrow="1" testId="report-overview">
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-2 text-sm">
        <div className="text-xl font-extrabold" data-testid="report-restaurant-name">{r.name}</div>
        <div className="flex gap-2 text-slate-700" data-testid="report-address"><MapPin className="mt-0.5 h-4 w-4 shrink-0" />{r.address}</div>
        {r.phone && <div className="flex gap-2" data-testid="report-phone"><Phone className="h-4 w-4" />{r.phone}</div>}
        {r.website && <a className="flex gap-2 break-all hover:underline" href={r.website} target="_blank" rel="noreferrer" data-testid="report-website"><Globe className="h-4 w-4 shrink-0" />{r.website}</a>}
        {r.google_maps_uri && <a className="flex gap-2 hover:underline" href={r.google_maps_uri} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" />Google Maps listing</a>}
      </div>
      <div className="space-y-1 rounded-md bg-slate-50 p-3 font-mono text-[11px] text-slate-600">
        <div>Google Place ID: {r.place_id}</div>
        {r.lat && <div>Coordinates: {Number(r.lat).toFixed(5)}, {Number(r.lng).toFixed(5)}</div>}
        {r.primary_type && <div>Type: {r.primary_type}</div>}
        {r.business_status && <div>Business status: {r.business_status}</div>}
        {loc.rating != null && <div data-testid="report-rating">Google rating: {loc.rating} ({loc.review_count?.toLocaleString()} reviews)</div>}
        {loc.group && <div>Group: {loc.group.name} {loc.group_locations?.length ? `(+${loc.group_locations.length} other location(s) on file)` : ""}</div>}
        <div className="pt-1 font-sans text-[10px] text-slate-400">Listing data © Google Maps</div>
      </div>
    </div>
  </Section>
);

const Coverage = ({ rep }) => {
  const c = rep.coverage;
  return (
    <Section title="Research date & coverage" eyebrow="2" testId="report-coverage">
      <div className="flex flex-wrap gap-6 text-sm">
        <div><div className="text-xs text-slate-500">Researched</div><div className="font-semibold">{fmtDateTime(rep.created_at)}</div></div>
        <div><div className="text-xs text-slate-500">Report version</div><div className="font-semibold">v{rep.version}</div></div>
        <div><div className="text-xs text-slate-500">Coverage</div><div className={`font-semibold ${c.level === "Limited" ? "text-orange-700" : "text-slate-900"}`} data-testid="report-coverage-level">{c.level} coverage</div></div>
        <div><div className="text-xs text-slate-500">Website pages checked</div><div className="font-semibold">{c.pages_checked} ({c.pages_inaccessible} inaccessible)</div></div>
        <div><div className="text-xs text-slate-500">Web searches</div><div className="font-semibold">{c.searches_run}</div></div>
        <div><div className="text-xs text-slate-500">Evidence items</div><div className="font-semibold">{c.evidence_items}</div></div>
      </div>
      <ul className="mt-4 space-y-1 text-xs text-slate-600">
        {Object.entries(c.stages).map(([k, s]) => <li key={k}><span className="font-mono font-semibold uppercase">{k}</span> · {s.status} {s.message ? `— ${s.message}` : ""}</li>)}
      </ul>
      {c.level === "Limited" && <p className="mt-3 rounded-md bg-orange-50 p-2 text-xs text-orange-800">This is not a comprehensive audit. Several sources were unavailable or not configured; see Sources & limitations.</p>}
    </Section>
  );
};

const Stack = ({ items }) => (
  <Section title="What the restaurant currently uses" eyebrow="4" testId="report-current-stack">
    {items.length === 0 ? <p className="text-sm text-slate-500">No technology providers were identified from the checked sources. This does not mean none are used.</p> : (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((s, i) => (
          <div key={i} className="rounded-md border p-3" data-testid={`stack-item-${i}`}>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{s.label}</div>
            <div className="mt-0.5 font-semibold">{s.value}</div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2"><EvidenceBadge status={s.status} />{s.source_url && <a href={s.source_url} target="_blank" rel="noreferrer" className="text-[11px] underline">source</a>}</div>
          </div>
        ))}
      </div>
    )}
  </Section>
);

const Findings = ({ findings, editable, onChanged }) => {
  const byCat = CATEGORY_ORDER.map((c) => [c, findings.filter((f) => f.category === c)]).filter(([, l]) => l.length);
  return (
    <Section title="Verified public findings by category" eyebrow="5" testId="report-findings">
      <div className="space-y-6">
        {byCat.map(([c, list]) => (
          <div key={c} data-testid={`findings-category-${c}`}>
            <h3 className="mb-1 border-b-2 border-slate-900 pb-1 text-sm font-bold uppercase tracking-wide">{list[0].category_label}</h3>
            {list.map((f) => <FindingRow key={f.id || f.key} f={f} editable={editable} onChanged={onChanged} />)}
          </div>
        ))}
      </div>
    </Section>
  );
};

const Opportunities = ({ opps, audience, services, enabled }) => (
  <Section title="Top opportunities" eyebrow="6" testId="report-opportunities">
    {!enabled?.length && <p className="mb-3 rounded-md bg-slate-50 p-2 text-xs text-slate-600" data-testid="opps-services-disabled-note">Service recommendations are off until an admin confirms availability in the Service Catalog. Opportunities below are based on evidence only.</p>}
    {opps.length === 0 ? <p className="text-sm text-slate-500" data-testid="opps-empty">The evidence collected does not support a specific opportunity yet.</p> : (
      <div className="grid gap-4 lg:grid-cols-3">
        {opps.map((o, i) => (
          <div key={i} className="flex flex-col rounded-lg border-l-4 border-orange-500 bg-white p-4 ring-1 ring-slate-200" data-testid={`opportunity-${i}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 font-semibold"><Lightbulb className="h-4 w-4 shrink-0 text-orange-600" />{o.title}</div>
              {audience === "internal" && o.priority && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase">{o.priority}</span>}
            </div>
            <dl className="mt-3 space-y-2 text-xs">
              <div><dt className="font-semibold text-slate-500">Observed evidence</dt><dd>{o.observed_evidence}</dd></div>
              <div><dt className="font-semibold text-slate-500">Potential implication (inference)</dt><dd className="italic">{o.implication_inference}</dd></div>
              {o.service_key && <div><dt className="font-semibold text-slate-500">Relevant service</dt><dd className="font-semibold">{services[o.service_key] || o.service_key}</dd></div>}
              {o.why_fit && <div><dt className="font-semibold text-slate-500">Why it fits</dt><dd>{o.why_fit}</dd></div>}
              {o.prerequisites && <div><dt className="font-semibold text-slate-500">Prerequisites</dt><dd>{o.prerequisites}</dd></div>}
              {o.needs_confirmation && <div><dt className="font-semibold text-slate-500">Needs confirmation</dt><dd>{o.needs_confirmation}</dd></div>}
              {audience === "internal" && o.next_action && <div><dt className="font-semibold text-slate-500">Suggested next action</dt><dd>{o.next_action}</dd></div>}
            </dl>
            <div className="mt-auto pt-3 text-[11px]">{(o.sources || []).map((s) => <a key={s.eid} href={s.url} target="_blank" rel="noreferrer" className="mr-2 underline">{s.eid}</a>)}</div>
          </div>
        ))}
      </div>
    )}
  </Section>
);

const Unknowns = ({ findings, questions }) => {
  const unk = findings.filter((f) => ["Unable to assess", "Not found in checked sources"].includes(f.status));
  return (
    <Section title="Unknowns & discovery questions" eyebrow="7" testId="report-unknowns">
      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <h3 className="mb-2 flex items-center gap-1 text-sm font-semibold"><ShieldAlert className="h-4 w-4" />Not established by public research ({unk.length})</h3>
          <ul className="space-y-1 text-xs text-slate-600">{unk.map((f) => <li key={f.key}><b>{f.item}</b> — {f.status}</li>)}</ul>
        </div>
        <div>
          <h3 className="mb-2 flex items-center gap-1 text-sm font-semibold"><HelpCircle className="h-4 w-4" />Questions to ask</h3>
          <ol className="list-decimal space-y-1 pl-4 text-sm">{[...questions, ...unk.filter((f) => f.confirm_question).slice(0, 6).map((f) => f.confirm_question)].filter((q, i, a) => a.indexOf(q) === i).map((q, i) => <li key={i}>{q}</li>)}</ol>
        </div>
      </div>
    </Section>
  );
};

const Sources = ({ rep }) => (
  <Section title="Sources & limitations" eyebrow="8" testId="report-sources">
    <ul className="mb-4 list-disc space-y-1 pl-5 text-xs text-slate-700">{rep.limitations.map((l, i) => <li key={i}>{l}</li>)}</ul>
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead><tr className="border-b text-slate-500"><th className="py-1.5 pr-2">ID</th><th className="pr-2">Type</th><th className="pr-2">Source</th><th className="pr-2">Match</th><th>Retrieved</th></tr></thead>
        <tbody>
          {rep.sources.map((s) => (
            <tr key={s.eid} className="border-b align-top" data-testid={`source-row-${s.eid}`}>
              <td className="py-1.5 pr-2 font-mono">{s.eid}</td>
              <td className="pr-2">{s.kind.replace(/_/g, " ")}</td>
              <td className="max-w-[420px] pr-2">{s.url ? <a href={s.url} target="_blank" rel="noreferrer" className="break-all underline">{s.title || s.url}</a> : s.title}{s.excerpt && <div className="text-slate-500">{s.excerpt}</div>}</td>
              <td className="pr-2">{s.match}</td>
              <td className="whitespace-nowrap">{fmtDate(s.retrieved_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Section>
);

export default function ReportView({ data, audience = "internal", editable = false, onChanged, services = {} }) {
  const { report: rep, location: loc, findings } = data;
  return (
    <div className="space-y-6" data-testid={`report-view-${audience}`}>
      <Overview r={rep.restaurant} loc={loc} />
      <Coverage rep={rep} />
      <Section title="Executive summary" eyebrow="3" testId="report-summary"><p className="text-sm leading-relaxed text-slate-800">{rep.executive_summary}</p></Section>
      <Stack items={rep.current_stack} />
      <Findings findings={findings} editable={editable} onChanged={onChanged} />
      <Opportunities opps={rep.opportunities} audience={audience} services={services} enabled={rep.services_enabled} />
      {audience === "internal" && <ContactsSection locationId={loc.id} editable={editable} />}
      <Unknowns findings={findings} questions={rep.discovery_questions} />
      <Sources rep={rep} />
    </div>
  );
}
